/**
 * Funzioni per il rilevamento di file HEIC/HEIF
 */

import {
  FTYP_OFFSET,
  HEIC_BRAND_IDENTIFIERS,
  HEIC_EXTENSIONS,
  HEIC_HEADER_SIZE,
  HEIC_MIME_TYPES,
} from './constants';

/**
 * Verifica se il file è in formato HEIC/HEIF basandosi su MIME type o estensione.
 * Controllo veloce che non richiede lettura del contenuto.
 *
 * @param file - Il file da verificare
 * @returns true se il file sembra essere HEIC (basato su metadati)
 */
export function isHeicFile(file: File): boolean {
  return isHeicByMimeType(file.type) || isHeicByExtension(file.name);
}

/**
 * Verifica se il MIME type corrisponde a HEIC/HEIF
 */
function isHeicByMimeType(mimeType: string): boolean {
  const normalizedType = mimeType.toLowerCase();
  return (HEIC_MIME_TYPES as readonly string[]).includes(normalizedType);
}

/**
 * Verifica se l'estensione del file corrisponde a HEIC/HEIF
 */
function isHeicByExtension(fileName: string): boolean {
  const normalizedName = fileName.toLowerCase();
  return HEIC_EXTENSIONS.some((ext) => normalizedName.endsWith(ext));
}

/**
 * Verifica i magic bytes per determinare se è effettivamente un file HEIC/HEIF.
 * Questo controllo è più accurato ma richiede la lettura del file.
 *
 * I file HEIC hanno la struttura:
 * - Offset 4-7: 'ftyp' (file type box identifier)
 * - Offset 8-11: brand identifier (heic, heix, hevc, etc.)
 *
 * @param file - Il file da verificare
 * @returns true se il file ha gli header HEIC validi
 */
export async function verifyHeicMagicBytes(file: File): Promise<boolean> {
  const headerBytes = await readFileHeader(file);

  if (!hasMinimumHeaderSize(headerBytes)) {
    return false;
  }

  if (!hasFtypSignature(headerBytes)) {
    return false;
  }

  const brand = extractBrandIdentifier(headerBytes);
  return isValidHeicBrand(brand);
}

/**
 * Legge i primi bytes del file necessari per l'header check
 */
async function readFileHeader(file: File): Promise<Uint8Array> {
  const buffer = await file.slice(0, HEIC_HEADER_SIZE).arrayBuffer();
  return new Uint8Array(buffer);
}

/**
 * Verifica che l'header abbia la dimensione minima richiesta
 */
function hasMinimumHeaderSize(bytes: Uint8Array): boolean {
  return bytes.length >= HEIC_HEADER_SIZE;
}

/**
 * Verifica la presenza del ftyp signature (file type box)
 */
function hasFtypSignature(bytes: Uint8Array): boolean {
  const expectedSignature = [0x66, 0x74, 0x79, 0x70]; // 'ftyp' in ASCII

  return expectedSignature.every((byte, index) => bytes[FTYP_OFFSET + index] === byte);
}

/**
 * Estrae il brand identifier dall'header
 */
function extractBrandIdentifier(bytes: Uint8Array): string {
  const brandOffset = 8;
  const brandLength = 4;

  return String.fromCharCode(...Array.from(bytes.slice(brandOffset, brandOffset + brandLength)));
}

/**
 * Verifica se il brand è un identificatore HEIC valido
 */
function isValidHeicBrand(brand: string): boolean {
  return (HEIC_BRAND_IDENTIFIERS as readonly string[]).includes(brand);
}
